import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';

/**
 * Declaring an emergency, and charging it to a bet.
 *
 * Naming the interrupted bet is required, and that requirement is the feature. An
 * emergency flag on its own is just a priority label with a louder colour; charged
 * against a specific bet, it shows up on that bet's card and on the cycle
 * scoreboard, so the team can see later what interrupting themselves cost them.
 *
 * The running count against budget is shown before confirming rather than after,
 * because "this would be the fourth this cycle" is the sentence most likely to
 * make someone reconsider whether this really is one.
 */
const DeclareEmergencyDialog = ({
  open,
  bug = null,
  bets = [],
  emergencyCount = 0,
  budget = 2,
  saving = false,
  onClose,
  onSubmit,
}) => {
  const [bet, setBet] = useState(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) return;
    setBet(null);
    setReason('');
  }, [open]);

  const wouldBe = emergencyCount + 1;
  const overBudget = wouldBe > budget;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Declare an emergency</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {bug?.title}
        </DialogContentText>

        <Autocomplete
          options={bets}
          value={bet}
          onChange={(e, next) => setBet(next)}
          disabled={saving}
          getOptionLabel={(option) => option.name || 'Untitled bet'}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          renderInput={(params) => (
            <TextField
              {...params}
              autoFocus
              label="Which bet this interrupts"
              helperText="Required. Somebody's cycle is paying for this, and the bet is who."
            />
          )}
        />

        <TextField
          fullWidth
          margin="normal"
          multiline
          minRows={3}
          label="Why this cannot wait for cool-down"
          placeholder="Payments are failing for everyone on the new checkout."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={saving}
        />

        {overBudget ? (
          <Alert severity="warning">
            This would be emergency {wouldBe} of a budget of {budget} this cycle.
            Nothing stops you — but a cycle that keeps getting interrupted is telling
            you something about the cycle, not about this bug.
          </Alert>
        ) : (
          <Typography variant="caption" color="text.secondary">
            {emergencyCount === 0
              ? 'First emergency this cycle.'
              : `Emergency ${wouldBe} of ${budget} this cycle.`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          color="error"
          disabled={saving || !bet || !reason.trim()}
          onClick={() => onSubmit({ interruptedBetId: bet.id, reason: reason.trim() })}
        >
          Declare it
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeclareEmergencyDialog;
